import { decode } from "html-entities";
import store from "../redux/store";
import { client, subdomain, isDevEnv } from "../config";

export const jsonParser = (_json) => {
  if (!_json) return {};

  if (typeof _json === "object") return _json;

  try {
    const parsed = JSON.parse(_json);
    return parsed ?? {};
  } catch (error) {
    return {};
  }
};

export const decodeHtmlEntities = (label) => {
  let item = label;

  try {
    item = decode(item);
  } catch (error) {
    // console.log('Cannot decode categories');
    return label;
  }

  return item;
};

// Advanced-slide iframes load from the prod files host, so on localhost they
// are cross-origin and reading their window/document throws CORS errors on
// every slide change. Detect localhost so iframe access can bail out there.
export const isLocalhost = () => {
  const h = window?.location?.hostname || "";
  return h === "localhost" || h === "127.0.0.1" || h.endsWith(".localhost");
};

export const getFrameWindow = (iframe) => {
  // Don't reach into cross-origin (prod) iframes from localhost — the reads
  // below throw CORS SecurityErrors. Callers all no-op on an undefined window.
  if (isLocalhost()) return undefined;

  let doc;

  if (iframe.contentWindow) {
    return iframe.contentWindow;
  }

  if (iframe.window) {
    return iframe.window;
  }

  if (!doc && iframe.contentDocument) {
    doc = iframe.contentDocument;
  }

  if (!doc && iframe.document) {
    doc = iframe.document;
  }

  if (doc && doc.defaultView) {
    return doc.defaultView;
  }

  if (doc && doc.parentWindow) {
    return doc.parentWindow;
  }

  return undefined;
};

export const runAnimation = (iframe) => {
  if (isDevEnv) {
    return;
  }

  var interval;
  var iteration = 0;

  if (iframe) {
    const iframe_window = getFrameWindow(iframe);

    if (iframe_window) {
      const iframe_document = iframe.contentDocument
        ? iframe.contentDocument
        : iframe.contentWindow
          ? iframe.contentWindow.document
          : iframe.document;

      interval = setInterval(function () {
        if (iframe_document.readyState === "complete") {
          if (typeof iframe_window.runAnimation !== "undefined") {
            clearInterval(interval);
            // fail safe for widescreen class
            iframe_window.document.body?.classList?.add("widescreen");
            iframe_window.runAnimation();
          } else {
            if (iteration === 50) {
              clearInterval(interval);
            }
            iteration++;
          }
        }
      }, 100);
    }
  }
};

export const runFragment = (iframe, fragmentStep) => {
  // if (isDevEnv) {
  //   return;
  // }


  if (iframe) {
    const iframe_window = getFrameWindow(iframe);

    if (iframe_window) {
      iframe_window.postMessage({ type: "RUN_FRAGMENT", fragmentStep }, "*");
    }
  }
};

export const addWidescreen = (iframe) => {
  if (isDevEnv) {
    return;
  }

  var interval;
  var iteration = 0;

  if (iframe) {
    const iframe_window = getFrameWindow(iframe);

    if (iframe_window) {
      const iframe_document = iframe.contentDocument
        ? iframe.contentDocument
        : iframe.contentWindow
          ? iframe.contentWindow.document
          : iframe.document;

      interval = setInterval(function () {
        if (iframe_document.readyState === "complete") {
          if (typeof iframe_window.document?.body !== "undefined") {
            clearInterval(interval);
            iframe_window.document.body?.classList?.add("widescreen");
          } else {
            if (iteration === 10) {
              clearInterval(interval);
            }

            iteration++;
          }
        }
      }, 500);
    }
  }
};

export const iframeClickEvents = (iframe) => {
  if (isDevEnv) {
    return;
  }

  if (iframe) {
    const iframe_window = getFrameWindow(iframe);

    if (iframe_window) {
      const iframe_document = iframe.contentDocument
        ? iframe.contentDocument
        : iframe.contentWindow
          ? iframe.contentWindow.document
          : iframe.document;

      var interval;
      interval = setInterval(function () {
        if (
          iframe_document.readyState === "complete" ||
          iframe_document.readyState === "interactive"
        ) {
          clearInterval(interval);
          iframe_window.document.addEventListener("click", clickEvents, false);
        }
      }, 500);
    }
  }
};

export const clickEvents = (event) => {
  const link = event?.target?.closest("a");

  if (link && link.href) {
    const href = link.href.toLowerCase();

    if (
      href.indexOf("gotoslide") > -1 ||
      href.indexOf("reloadslide") > -1 ||
      href.indexOf("opendocument") > -1 ||
      href.indexOf("openexternal") > -1 ||
      href.indexOf("openViewerMenu") > -1
    ) {
      event.stopPropagation();
      event.preventDefault();

      if (href.indexOf("openexternal") > -1) {
        let linkToOpen = href;
        linkToOpen = linkToOpen.split("link=");
        linkToOpen = linkToOpen[1];
        window.open(linkToOpen);
      }
    } else {
      const url = new URL(link.href);

      if (url.origin !== window.location.origin) {
        event.stopPropagation();
        event.preventDefault();
        window.open(url);
      }
    }

    return false;
  }
};

export const getThemeJson = (theme_label) => {
  const state = store.getState();
  const themes = state?.themesReducer?.themes
    ? state?.themesReducer?.themes
    : {};
  let json = {};

  if (theme_label) {
    json = themes[theme_label];

    if (json) {
      try {
        json = JSON.parse(json);
      } catch (error) {
        // some error with parsing
        console.error("App", "Theme parse error!");
      }
    }
  }
  return json;
};

export const getThemeIds = (slide_layout) => {
  const state = store.getState();
  const themes = state?.themesReducer?.themes
    ? state?.themesReducer?.themes
    : {};
  let allowedThemes = [];
  if (themes) {
    const themeLabels = Object.keys(themes);
    let themeItems = [];
    themeLabels.forEach((e) => {
      let data = null;
      try {
        data = themes[e] ? JSON.parse(themes[e]) : null;
      } catch (err) {
        console.error("App", "Theme parse error!", e);
      }
      if (!data || !Array.isArray(data.layouts)) return;
      themeItems.push({ label: e, data });
    });
    if (themeItems.length > 0) {
      themeItems.forEach((e) => {
        let result = e.data.layouts.find((e) => e.id === slide_layout);
        if (result) {
          allowedThemes.push(e.label);
        }
      });
    }
  }
  return allowedThemes;
};

export const getLayoutIcons = (layout_id, theme_or_label) => {
  let icons;
  const theme_json =
    typeof theme_or_label === "string"
      ? getThemeJson(theme_or_label)
      : theme_or_label;

  theme_json?.layouts.forEach((layout) => {
    if (layout.id === layout_id) {
      icons = layout.icons;
    }
  });

  return icons;
};

export const getLayoutFiles = (layout_id, theme_or_label) => {
  let files;
  const theme_json =
    typeof theme_or_label === "string"
      ? getThemeJson(theme_or_label)
      : theme_or_label;

  theme_json?.layouts.forEach((layout) => {
    if (layout.id === layout_id) {
      files = layout.files;
    }
  });

  return files;
};

export const getDefaultBlockContent = (slideLayoutId, block, themeJson) => {
  const slideLayout = themeJson?.layouts?.find((e) => {
    return e.id === slideLayoutId;
  });

  console.log('slidelayout for: ', slideLayoutId, ' - ',slideLayout, ' - themejson: ', themeJson);

  if (slideLayout.hasOwnProperty("defaultBlockContent")) {
    const defaultBlockContent = slideLayout.defaultBlockContent;
    if (defaultBlockContent.hasOwnProperty(block)) {
      if (
        subdomain.includes("dev") &&
        defaultBlockContent[block].hasOwnProperty("dev")
      ) {
        return defaultBlockContent[block].dev;
      } else {
        if (defaultBlockContent[block].hasOwnProperty("prod")) {
          return defaultBlockContent[block].prod;
        } else {
          return defaultBlockContent[block];
        }
      }
    }
  }
  return "";
};

export const getThemeBackgrounds = (theme_label) => {
  let colors = [];
  const theme_json = getThemeJson(theme_label);

  if (theme_json?.backgroundColors) {
    colors = theme_json.backgroundColors;
  }

  return colors;
};

export const getParamFromURL = (url, parameter) => {
  const urlParams = new URLSearchParams(url);
  const myParam = urlParams.get(parameter);

  return myParam;
};

export const processObject = (obj) => {
  for (let key in obj) {
    if (typeof obj[key] === "object" && obj[key] !== null) {
      // Recursively process nested objects
      obj[key] = processObject(obj[key]);
    } else if (typeof obj[key] === "string") {
      // Perform operations on string values
      obj[key] = decode_utf8(obj[key]);
    }
  }
  return obj;
};

/**
 * Deprecated use of escape
 */
export const decode_utf8 = (string) => {
  try {
    return decodeURIComponent(escape(string));
  } catch (e) {
    return string;
  }
};

export const isValidJson = (json) => {
  try {
    JSON.parse(json);
    return true;
  } catch (e) {
    return false;
  }
};

export const isSafari = /^((?!chrome|android).)*safari/i.test(
  navigator.userAgent,
);

// Format date
export const formatTime = (time, shortDay, shortMonth, shortYear) => {
  let timestamp;

  const nthNumber = (number) => {
    if (shortDay) return "";
    if (number > 3 && number < 21) return "th";
    switch (number % 10) {
      case 1:
        return "st";
      case 2:
        return "nd";
      case 3:
        return "rd";
      default:
        return "th";
    }
  };

  const dateCreated = new Date(time * 1000);

  if (client === "equans") {
    // Convert day to a number to remove leading zero
    const day = parseInt(
      dateCreated.toLocaleString("en-GB", { day: "2-digit" }),
      10,
    );
    const month = dateCreated.toLocaleString("en-GB", {
      month: shortMonth ? "short" : "long",
    });
    const year = dateCreated.toLocaleString("en-GB", {
      year: shortYear ? "2-digit" : "numeric",
    });

    timestamp = `${day} ${month} ${year}`;
  } else {
    const day = dateCreated.toLocaleString("en-GB", { day: "2-digit" });
    const month = dateCreated.toLocaleString("en-GB", {
      month: shortMonth ? "short" : "long",
    });
    const year = dateCreated.toLocaleString("en-GB", {
      year: shortYear ? "2-digit" : "numeric",
    });

    timestamp = `${day}${nthNumber(parseInt(day, 10))} ${month} ${year}`;
  }

  return timestamp;
};

// Check if item exists in array for favorites
export const checkIfFavorite = (array, _id, contentType) => {
  const id = parseInt(_id, 10);

  let foundObject = false;
  if (array.length > 0) {
    foundObject = array.find((obj) => parseInt(obj.id) === id);
  }
  return foundObject ? foundObject.favorite : false;
};

export const getFavoritesByContentType = (
  favorites,
  contentType,
  returnCommaSeparatedString,
) => {
  if (favorites.length > 0) {
    const filteredFavorites = favorites.filter(
      (fav) => fav.contentType === contentType && fav.favorite === true,
    );
    if (returnCommaSeparatedString) {
      return filteredFavorites.map((fav) => fav.id).join(",");
    } else {
      return filteredFavorites;
    }
  } else return returnCommaSeparatedString ? "" : [];
};

export const generateStateToken = () => {
  const array = new Uint8Array(32);
  for (let i = 0; i < 32; i++) {
    array[i] = Math.floor(Math.random() * 256);
  }
  const hexString = Array.from(array)
    .map((byte) => byte.toString(16).padStart(2, "0"))
    .join("");
  return hexString;
};

export const hexToRgbA = (hex, alpha) => {
  let c;
  if (/^#([A-Fa-f0-9]{3}){1,2}$/.test(hex)) {
    c = hex.substring(1).split("");
    if (c.length === 3) {
      c = [c[0], c[0], c[1], c[1], c[2], c[2]];
    }
    c = "0x" + c.join("");
    return (
      "rgba(" +
      [(c >> 16) & 255, (c >> 8) & 255, c & 255].join(",") +
      "," +
      alpha +
      ")"
    );
  }
  throw new Error("Bad Hex");
};

export const buildReactions = (data, user) => {
  let likes = [];
  let love = [];
  let thanks = [];
  let insightful = [];
  let surprised = [];
  let laugh = [];

  let hasReacted = false;
  let reactionType = "none";
  let reactionId = "none";

  if (data.rows && data.rows.length > 0) {
    data.rows.forEach((row) => {
      // user_id comes back as a string; current_user.uid may be numeric.
      if (String(row.user_id) === String(user?.current_user?.uid)) {
        hasReacted = true;
        reactionType = row.type;
        reactionId = row.id;
      }

      if (row.type === "reaction_like") {
        likes.push(row);
      }

      if (row.type === "reaction_laughing") {
        laugh.push(row);
      }

      if (row.type === "reaction_love") {
        love.push(row);
      }

      if (row.type === "reaction_surprised") {
        surprised.push(row);
      }

      if (row.type === "reaction_thanks") {
        thanks.push(row);
      }

      if (row.type === "reaction_insightful") {
        insightful.push(row);
      }
    });
  }

  return {
    hasReacted: hasReacted,
    reactionType: reactionType,
    reactionId: reactionId,
    likes: likes.length,
    love: love.length,
    surprised: surprised.length,
    thanks: thanks.length,
    insightful: insightful.length,
  };
};

export const favoritesContentType = (type) => {
  let types = {};

  if (client === "companyapp") {
    types = {
      news: "news",
      blogs: "blogs",
      event: "event",
      presentation: "presentation",
      video: "video",
      podcast: "podcast",
      course: "course",
      quiz: "quiz",
      document: "document",
      link: "link",
      poll: "poll",
      webform: "webform",
      company_directory: "company_directory",
      people: "people",
      forum: "forum",
    };
  } else {
    types = {
      news: "news",
      blogs: "blogs",
      event: "events",
      presentation: "presentation-item",
      video: "videos",
      podcast: "podcasts",
      course: "training",
      quiz: "quizzes",
      document: "documents",
      link: "links",
      poll: "polls",
      webform: "forms",
      company_directory: "company_directory",
      people: "people",
      forum: "forum",
    };
  }

  return type ? types[type] : null;
};

export const navigationLabels = (screen) => {
  const label = {
    home: "Home 1",
    home2: "Home 2",
    search: "Search",
    news: "Latest News",
    blogs: "Company Blogs",
    event: "Upcoming Events",
    presentation: "Presentations",
    video: "Video Library",
    podcast: "Podcasts",
    course: "Training Courses",
    quiz: "Test & Quizzes",
    document: "Documents",
    link: "Useful Links",
    poll: "Opinion Polls",
    webform: "Surveys & Forms",
    company_directory: "Company Directory",
    people: "User Directory",
    forum: "Discussion Boards",
    gallery: "Galleries",
  };

  return label[screen] || "Unknown Screen";
};

export const taxonomyByClient = () => {
  if (client === "companyapp") {
    return {
      course: "training_categories",
    };
  } else {
    return {
      course: "course_categories",
    };
  }
};

export const processCellStyle = (
  rowCount,
  rowIndex,
  cellCount,
  cellIndex,
  block,
  slideLayoutId,
) => {
  if (block.data?.styled && slideLayoutId === "custom") {
    const styled = block.data?.styled;
    const {
      tableStyle,
      backgroundColor,
      secondaryBGColor,
      fontColor,
      fontSize,
      fontWeight,
      firstRowBGColor,
      firstColBGColor,
      firstRowFontColor,
      firstRowFontSize,
      firstRowFontWeight,
      firstColFontColor,
      firstColFontSize,
      firstColFontWeight,
      gridColor,
      gridType,
      horizontalAlign = "start",
      verticalAlign = "start",
      firstRowHorizontalAlign = "start",
      firstRowVerticalAlign = "start",
      firstColHorizontalAlign = "start",
      firstColVerticalAlign = "start",
    } = styled;
    let cellStyle = "";
    const addStyle = (styleString) =>
      (cellStyle = cellStyle + `${styleString};`);
    const getFontWeight = (fontWeightValue) =>
      fontWeightValue !== 1 ? (fontWeightValue === 0 ? 700 : 300) : "";

    const defaultBgColor = () => {
      if (tableStyle === 0) {
        if (backgroundColor !== "") {
          addStyle(`background-color: ${backgroundColor}`);
        }
      } else if (tableStyle === 1) {
        if (rowIndex % 2 === 0) {
          if (backgroundColor !== "") {
            addStyle(`background-color: ${backgroundColor}`);
          }
        } else {
          if (secondaryBGColor !== "") {
            addStyle(`background-color: ${secondaryBGColor}`);
          }
        }
      } else if (tableStyle === 2) {
        if (cellIndex % 2 === 0) {
          if (backgroundColor !== "") {
            addStyle(`background-color: ${backgroundColor}`);
          }
        } else {
          if (secondaryBGColor !== "") {
            addStyle(`background-color: ${secondaryBGColor}`);
          }
        }
      }
    };
    const defaultFontColor = () =>
      fontColor !== "" ? addStyle(`color: ${fontColor}`) : "";
    const defaultFontSize = () =>
      fontSize !== "" ? addStyle(`font-size: ${fontSize}`) : "";
    const defaultFontWeight = () =>
      fontWeight !== 1
        ? addStyle(`font-weight: ${getFontWeight(fontWeight)}`)
        : "";

    if (rowIndex === 0) {
      // First row styles
      if (firstRowBGColor !== "") {
        addStyle(`background-color: ${firstRowBGColor}`);
      } else {
        if (cellIndex === 0 && firstColBGColor) {
          addStyle(`background-color: ${firstColBGColor}`);
        } else {
          defaultBgColor();
        }
      }
      if (firstRowFontColor !== "") {
        addStyle(`color: ${firstRowFontColor}`);
      } else defaultFontColor();
      if (firstRowFontSize !== "") {
        addStyle(`font-size: ${firstRowFontSize}`);
      } else defaultFontSize();
      if (firstRowFontWeight !== 1) {
        addStyle(`font-weight: ${getFontWeight(firstRowFontWeight)}`);
      } else defaultFontWeight();
      addStyle(`text-align: ${firstRowHorizontalAlign}`);
      addStyle(`justify-content: ${firstRowHorizontalAlign}`);
      addStyle(`align-items: ${firstRowVerticalAlign}`);
    } else if (rowIndex !== 0 && cellIndex === 0) {
      // First column styles
      if (firstColBGColor !== "") {
        addStyle(`background-color: ${firstColBGColor}`);
      } else defaultBgColor();
      if (firstColFontColor !== "") {
        addStyle(`color: ${firstColFontColor}`);
      } else defaultFontColor();
      if (firstColFontSize !== "") {
        addStyle(`font-size: ${firstColFontSize}`);
      } else defaultFontSize();
      if (firstColFontWeight !== 1) {
        addStyle(`font-weight: ${getFontWeight(firstColFontWeight)}`);
      } else defaultFontWeight();
      addStyle(`text-align: ${firstColHorizontalAlign}`);
      addStyle(`justify-content: ${firstColHorizontalAlign}`);
      addStyle(`align-items: ${firstColVerticalAlign}`);
    } else {
      defaultBgColor();
      defaultFontColor();
      defaultFontSize();
      defaultFontWeight();
      addStyle(`text-align: ${horizontalAlign}`);
      addStyle(`justify-content: ${horizontalAlign}`);
      addStyle(`align-items: ${verticalAlign}`);
    }
    const gridStyle = `1px solid ${gridColor}`;
    if (gridType === 1) {
      if (cellIndex === cellCount - 1) {
        addStyle(`border-right: ${gridStyle}`);
      }
      if (rowIndex === rowCount - 1) {
        addStyle(`border-bottom: ${gridStyle}`);
      }
      addStyle(`border-top: ${gridStyle}`);
      addStyle(`border-left: ${gridStyle}`);
    } else if (gridType === 2) {
      if (rowIndex === 0) {
        addStyle(`border-top: ${gridStyle}`);
        addStyle(`border-bottom: ${gridStyle}`);
      } else {
        addStyle(`border-bottom: ${gridStyle}`);
      }
    } else if (gridType === 3) {
      if (cellIndex === 0) {
        addStyle(`border-left: ${gridStyle}`);
        addStyle(`border-right: ${gridStyle}`);
      } else {
        addStyle(`border-right: ${gridStyle}`);
      }
    }
    return cellStyle;
  } else return "";
};

export const setLinkTag = (rel, href, attributes = {}) => {
  let tag = document.querySelector(`link[rel='${rel}']`);
  if (!tag) {
    tag = document.createElement("link");
    tag.rel = rel;
    document.head.appendChild(tag);
  }
  tag.href = href;
  Object.entries(attributes).forEach(([key, value]) =>
    tag.setAttribute(key, value),
  );
};

export const safeUpdateBlock = async (editor, blockId, newData) => {
  const savedData = await editor.save();
  const blockIndex = getBlockIndex(editor, blockId);
  const block = savedData.blocks[blockIndex];

  if (!block) return;

  editor.blocks.update(blockId, newData, block.tunes);
};

export const getBlockIndex = (editor, blockId) => {
  const count = editor.blocks.getBlocksCount();
  for (let i = 0; i < count; i++) {
    const block = editor.blocks.getBlockByIndex(i);
    if (block.id === blockId) {
      return i;
    }
  }

  return -1;
};

export const screenOptions = [
  { label: "Blog", value: "BlogSingle" },
  { label: "Blog List", value: "BlogStack" },
  { label: "Directory", value: "DirectorySingle" },
  { label: "Directory List", value: "DirectoryStack" },
  { label: "Course", value: "TrainingSingle" },
  { label: "Course List", value: "TrainingStack" },
  { label: "Document", value: "Viewer" },
  { label: "Documents List", value: "DocumentStack" },
  { label: "Event", value: "EventsSingle" },
  { label: "Events List", value: "EventsStack" },
  // { label: 'ForumTopics (forum)', value: 'ForumTopics' },
  // { label: 'ForumStack (forum)', value: 'ForumStack' },
  { label: "Link List", value: "LinkStack" },
  { label: "News", value: "NewsSingle" },
  { label: "News List", value: "NewsStack" },
  { label: "Podcast", value: "PodcastSingle" },
  { label: "Podcast List", value: "PodcastStack" },
  { label: "Poll", value: "PollViewer" },
  { label: "Poll List", value: "PollStack" },
  { label: "Presentation", value: "PresentationViewer" },
  { label: "Presentation List", value: "PresentationStack" },
  { label: "Home", value: "Home" },
  { label: "Video", value: "VideoSingle" },
  { label: "Video List", value: "VideoStack" },
  { label: "Quiz", value: "QuizSingle" },
  { label: "Quiz List", value: "QuizStack" },
  { label: "Contact", value: "ContactSingle" },
  { label: "Contact List", value: "ContactStack" },
  { label: "Form", value: "FormViewer" },
  { label: "Form List", value: "FormStack" },
  { label: "Gallery", value: "GallerySingle" },
  { label: "Gallery List", value: "GalleryStack" },
];

// Screens that are lists / index pages
const listScreens = screenOptions
  .filter((s) => s.value.endsWith("Stack"))
  .map((s) => s.value);

export const linkGenerator = (screen, id) => {
  if (!screen) return "/";

  let targetScreen = screen;

  // If the screen is a list and ID exists, switch to single/viewer
  if (id && listScreens.includes(screen)) {
    // Try replacing Stack with Single or Viewer
    const singleCandidate = screen.replace(/Stack$/, "Single");
    const viewerCandidate = screen.replace(/Stack$/, "Viewer");

    if (screenOptions.find((s) => s.value === singleCandidate)) {
      targetScreen = singleCandidate;
    } else if (screenOptions.find((s) => s.value === viewerCandidate)) {
      targetScreen = viewerCandidate;
    }
  }

  // Map the final screen to a route
  const routeMap = {
    BlogSingle: (id) => `/blogs/${id}`,
    BlogStack: () => `/blogs`,

    DirectorySingle: (id) => `/company_directory/${id}`,
    DirectoryStack: () => `/company_directory`,

    TrainingSingle: (id) => `/training/${id}`,
    TrainingStack: () => `/training`,

    Viewer: (id) => `/documents/${id}`,
    DocumentStack: () => `/documents`,

    EventsSingle: (id) => `/events/${id}`,
    EventsStack: () => `/events`,

    LinkStack: () => `/links`,

    NewsSingle: (id) => `/news/${id}`,
    NewsStack: () => `/news`,

    PodcastSingle: (id) => `/podcasts/${id}`,
    PodcastStack: () => `/podcasts`,

    PollViewer: (id) => `/polls/${id}`,
    PollStack: () => `/polls`,

    PresentationViewer: (id) => `/presentations/view/${id}`,
    PresentationStack: () => `/presentations`,

    Home: () => `/`,

    VideoSingle: (id) => `/videos/${id}`,
    VideoStack: () => `/videos`,

    QuizSingle: (id) => `/quizzes/${id}`,
    QuizStack: () => `/quizzes`,

    ContactSingle: (id) => `/contacts/${id}`,
    ContactStack: () => `/contacts`,

    FormViewer: (id) => `/forms/${id}`,
    FormStack: () => `/forms`,

    GallerySingle: (id) => `/galleries/${id}`,
    GalleryStack: () => `/galleries`,
  };

  const resolver = routeMap[targetScreen];
  if (!resolver) {
    console.warn("No route mapped for screen:", targetScreen);
    return "/";
  }

  return id ? resolver(id) : resolver();
};

export const addLineBreak = (element) => {
  const selection = window.getSelection(); // get the current selection.
  const range = selection.getRangeAt(0); // get the current range of the selection.
  const brTags = element.querySelectorAll("br");

  // we need to create 2x tags initially, 1x tag will not do anything
  // this is the default browser behavior with Enter + Shift as well
  if (brTags.length === 0) {
    const br1 = document.createElement("br");
    const br2 = document.createElement("br");
    range.insertNode(br1); // inserts the <br> element at the current cursor position.
    range.insertNode(br2); // inserts the <br> element at the current cursor position.
    range.setStartAfter(br2); // set the start of the range to the position after the <br> element.
  } else {
    const br = document.createElement("br");
    range.insertNode(br);
    range.setStartAfter(br); // set the start of the range to the position after the <br> element.
  }

  range.collapse(true); // collapse the range to the position after the <br> element.
  selection.removeAllRanges(); // remove any existing ranges from the selection.
  selection.addRange(range); // add the modified range to the selection.
};

export const humanFileSize = (bytes, si = true, dp = 1) => {
  const thresh = si ? 1000 : 1024;

  if (Math.abs(bytes) < thresh) {
    return bytes + " B";
  }

  const units = si
    ? ["KB", "MB", "GB", "TB", "PB", "EB", "ZB", "YB"]
    : ["KiB", "MiB", "GiB", "TiB", "PiB", "EiB", "ZiB", "YiB"];
  let u = -1;
  const r = 10 ** dp;

  do {
    bytes /= thresh;
    ++u;
  } while (
    Math.round(Math.abs(bytes) * r) / r >= thresh &&
    u < units.length - 1
  );

  return bytes.toFixed(dp) + " " + units[u];
};
